import {
  MilestoneRoleDisplay,
  ProjectLeaderRoleDisplay,
  UserSelect,
} from "@/containers/Project/components";
import * as S from "@/containers/Project/components/styled";
import { GUTTER_SIZE, ProjectUserPermissions } from "@/enums";
import { projectMilestoneType } from "@/propTypes";
import { useProjectContext } from "@/providers";
import {
  useGetAllNotificationGroupsQuery,
  useGetFormLookupsQuery,
} from "@/services";
import { Button, Col, Form, Row, Space, Switch, Typography } from "antd";
import _ from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";

export const MilestoneNotificationCard = ({ milestone }) => {
  const [form] = Form.useForm();
  const intl = useIntl();
  const { updateMilestone, updatingMilestone, canUser } = useProjectContext();

  const { data: notificationGroups, isFetching: isLoadingNotificationGroups } =
    useGetAllNotificationGroupsQuery();

  const [regions, setRegions] = useState(null);

  const { data: forms } = useGetFormLookupsQuery();

  useEffect(() => {
    setRegions(forms?.intake?.region);
  }, [forms?.intake]);

  const getNotificationUserIds = useCallback(
    (approvers) =>
      approvers
        ?.filter((x) => !x.regionId)
        ?.map((x) => x.assignedUsers?.map((x) => x.userId))
        .flat(),
    []
  );

  if (!regions) {
    return <Form form={form} component={false} />;
  }

  const renderToggle = (value, key, disabled) => (
    <Form.Item name={key} valuePropName="checked" initialValue={value ?? true}>
      <Switch
        disabled={
          disabled || !canUser(ProjectUserPermissions.EditNotified, milestone)
        }
        checkedChildren={intl.formatMessage({
          id: "general.yes",
        })}
        unCheckedChildren={intl.formatMessage({
          id: "general.no",
        })}
      ></Switch>
    </Form.Item>
  );

  const renderGroups = () => {
    if (
      !milestone.notification?.groups?.length &&
      !milestone.notification?.isCoreTeamIncluded
    )
      return null;

    const groupDict = _.keyBy(notificationGroups, "id");
    return (
      <Col flex="1">
        <S.MilestoneDetailSubCard
          styles={{ body: { padding: 0 }, header: { padding: 0 } }}
          title={
            <Typography.Title level={4}>
              {intl.formatMessage({
                id: "project.notifications.groups",
              })}
            </Typography.Title>
          }
          loading={isLoadingNotificationGroups}
          extra={
            <Typography.Text>
              {intl.formatMessage({
                id: "project.notifications.notifyToggle",
              })}
            </Typography.Text>
          }
        >
          {milestone.notification?.isCoreTeamIncluded && (
            <Row gutter={GUTTER_SIZE.small} justify="center" wrap={false}>
              <Col flex="auto">
                {intl.formatMessage({ id: "project.notifications.coreTeam" })}
              </Col>
              <Col>
                {renderToggle(
                  milestone.notification?.notifyCoreTeam,
                  "notifyCoreTeam",
                  milestone.notification?.isCoreTeamRequired
                )}
              </Col>
            </Row>
          )}
          {milestone.notification?.groups?.map((group) => (
            <Row gutter={GUTTER_SIZE.small} justify="center" key={group.id}>
              <Col flex="auto">{groupDict[group.groupId]?.name}</Col>
              <Col>
                {renderToggle(group.isNotify, group.id, group.isRequired)}
              </Col>
            </Row>
          ))}
        </S.MilestoneDetailSubCard>
      </Col>
    );
  };

  const renderAddUsers = () => {
    const notificationUsers = getNotificationUserIds(
      milestone.notification?.roles
    );

    return (
      <S.RegionDisplayCol flex="1">
        <S.MilestoneDetailSubCard
          styles={{ body: { padding: 0 }, header: { padding: 0 } }}
          title={
            <Typography.Title level={4}>
              {intl.formatMessage({
                id: "project.notifications.addUsersToNotify",
              })}
            </Typography.Title>
          }
        >
          <Form.Item
            label={intl.formatMessage({
              id: "project.notifications.nameInputField",
            })}
            name="notificationUsers"
            initialValue={notificationUsers}
          >
            <UserSelect
              users={notificationUsers}
              readOnly={
                !canUser(ProjectUserPermissions.EditNotified, milestone)
              }
            />
          </Form.Item>
        </S.MilestoneDetailSubCard>
      </S.RegionDisplayCol>
    );
  };

  const renderNotifiedUsers = () => {
    const regions = forms.intake?.region;
    return Object.keys(regions).map((region) => {
      const notificationRoles = milestone.notification?.roles?.filter(
        (x) => x.regionId === region
      );
      const regionLeaderNotification =
        milestone.projectLeaderNotifications?.find(
          (x) => x.regionId === region
        );

      if (!notificationRoles?.length && !regionLeaderNotification) return null;

      return (
        <S.RegionDisplayCol flex="1" key={regions[region].fallback}>
          <Typography.Title level={4}>
            {(regions || {})[region]?.fallback}
          </Typography.Title>
          <MilestoneRoleDisplay
            region={region}
            roles={notificationRoles}
            form={form}
            editMode={canUser(ProjectUserPermissions.EditNotified, milestone)}
          />
          {regionLeaderNotification && (
            <ProjectLeaderRoleDisplay
              role={regionLeaderNotification}
              form={form}
              status={milestone?.status}
            />
          )}
        </S.RegionDisplayCol>
      );
    });
  };

  const handleSave = () => {
    const formValues = form.getFieldsValue(true);
    const notificationUsersRole = {
      assignedUsers: formValues.notificationUsers.map((userId) => ({
        userId: userId,
      })),
    };

    const roles = milestone.notification?.roles
      ?.filter((x) => x.regionId)
      .map((role) => {
        if (!formValues[role.id]) return role;

        return {
          ...role,
          isNotApplicable:
            formValues[role.id].isNotApplicable ?? role.isNotApplicable,
          assignedUsers:
            formValues[role.id].assignedUsers?.map((x) => ({
              userId: x,
            })) || [],
        };
      });

    const updatedGroups = milestone.notification?.groups?.map((group) => ({
      ...group,
      isNotify: formValues[group.id],
    }));

    const leaderNotifications = milestone.projectLeaderNotifications?.map(
      (leaderRole) => {
        if (!formValues[leaderRole.id]) return leaderRole;

        return {
          ...leaderRole,
          IsNotApplicable:
            formValues[leaderRole.id].isNotApplicable ??
            leaderRole.isNotApplicable,
        };
      }
    );

    const NotifyCoreTeam = formValues.notifyCoreTeam;

    const updatedMilestone = {
      ...milestone,
      notification: {
        ...milestone.notification,
        roles: [...(roles || []), notificationUsersRole],
        groups: updatedGroups,
        notifyCoreTeam: NotifyCoreTeam,
      },
      projectLeaderNotifications: leaderNotifications,
    };

    updateMilestone(updatedMilestone);
  };

  return (
    <>
      <Form form={form} layout="vertical" autoComplete="off">
        <S.MilestoneDetailCard
          styles={{ body: { padding: 0 } }}
          extra={
            canUser(ProjectUserPermissions.EditNotified, milestone) && (
              <Button
                data-test-id="save-milestone-notification"
                onClick={handleSave}
                type="primary"
                loading={updatingMilestone}
              >
                {intl.formatMessage({ id: "general.save" })}
              </Button>
            )
          }
        >
          <Row gutter={GUTTER_SIZE.large}>
            <Space align="baseline" wrap size="large">
              {renderGroups()}
              {renderAddUsers()}
            </Space>
          </Row>
          <Row gutter={GUTTER_SIZE.large}>{renderNotifiedUsers()}</Row>
        </S.MilestoneDetailCard>
      </Form>
    </>
  );
};

MilestoneNotificationCard.propTypes = {
  milestone: projectMilestoneType.isRequired,
};
